/* eslint-disable max-len */
import React, { useContext } from 'react';
import {
  oneOf,
  bool as boolType,
  any,
  shape as shapeType,
  string as stringType,
} from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  QueryProvider
} from '@thd-nucleus/data-sources';
import classNames from 'classnames/bind';
import { ProductPod } from '@thd-olt-component-react/product-pod';
import {
  Col, Carousel, Checkbox
} from '@thd-olt-component-react/core-ui';
import { FBTContext } from '../contexts/FBTContext';
import { FBTPlaceholder } from '../FBTPlaceholder.component';
import {
  getTotalPrice,
  getCart,
  getAddToCartMessage
} from '../helpers/helpers';
import PriceMessage from '../Partials/PriceMessage.component';
import * as analytics from '../analytics/frequentlyboughttogether-analytics';
import styles from '../thd-recs-fbt.module.scss';

// components and runs into issues when they are in multiple places
// eslint-disable-next-line react/prop-types
const cx = classNames.bind(styles);
const Wrapper = ({ children }) => children;

const RecsFBTProductPodCarousel = (props) => {
  const { channel } = useContext(ExperienceContext);
  const {
    checkedItems,
    setCheckedItems,
    apiName,
  } = useContext(FBTContext);
  const isMobile = channel === 'mobile';

  const {
    data,
    storeId,
    itemId: anchorItemId,
    brandTitleMaxLine,
    customEventObject,
    hideATC,
    zipCode,
    hideBulkPrice,
    hideFulfillment,
    hideLimitPerOrder,
    hidePriceBadge,
    hideRating,
    hideRebates,
    hideSavingsText,
    hideTitle,
    hideShippingInfo,
    membershipInformation,
    hideShopThisCollection,
    showSeeMoreOptions,
    showSubscribeAndSave,
    slidesPer,
    keepAddToCart
  } = props;

  let hideAddToCart;

  if (keepAddToCart === true) {
    hideAddToCart = false; // show ATC always - including MW
  } else if (hideATC || isMobile) {
    hideAddToCart = true;
  }
  const cart = getCart(data, channel);
  const message = getAddToCartMessage(checkedItems?.length);
  let totalPrice = {};
  if (checkedItems?.length) {
    totalPrice = getTotalPrice(data);
  }

  const onAddToCartClick = () => {
    const { itemId } = props;
    let analyticsItems = [];
    // Uncheck all items on ATC callback.
    setCheckedItems([]);
    data.forEach((product) => {
      if (product.checked) {
        if (product.itemId !== itemId) {
          analyticsItems.push(product);
        }
      }
    });
    analytics.fbtAddToCartAnalytics({ analyticsItems });
  };

  const onChangeCheckbox = (id) => {
    const index = checkedItems.indexOf(id);
    if (index > -1) {
      setCheckedItems(checkedItems.filter((item) => item !== id));
    } else {
      setCheckedItems([...checkedItems, id]);
    }
  };

  let breakpoints = ({});
  if (!slidesPer) {
    breakpoints = ({
      sm: {
        slidesPerView: 2,
        slidesPerGroup: 2
      },
      md: {
        slidesPerView: 3,
        slidesPerGroup: 3
      },
      lg: {
        slidesPerView: 4,
        slidesPerGroup: 4
      },
      xl: {
        slidesPerView: 6,
        slidesPerGroup: 6
      }
    });
  }

  const defaultVariablesFBT = {
    skipSubscribeAndSave: true,
    skipSpecificationGroup: true,
    loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership || null,
    skipKPF: true,
    zipCode,
    storeId
  };

  return (
    <>
      {data?.length > 1 && <FBTPlaceholder hideTitle={hideTitle} />
      && (
        <Col>
          <Carousel
            breakpoints={breakpoints}
          >
            {data?.length > 1 && data.map((dynamicProduct, idx) => {
              const { product } = dynamicProduct;
              const { itemId } = product;
              const key = `fbt-product-line-${dynamicProduct.itemId}-${idx}`;
              return (
                <Wrapper key={idx}>
                  <QueryProvider
                    cacheKey={apiName}
                    dataSource={anchorItemId === itemId ? 'catalog' : apiName}
                    defaultVariables={defaultVariablesFBT}
                  >
                    <Checkbox
                      className={cx('frequently-bought-together__fbtProductLineItemClasses')}
                      key={key}
                      name={key}
                      id={dynamicProduct.itemId}
                      onChange={() => onChangeCheckbox(dynamicProduct.itemId)}
                      checked={dynamicProduct.checked}
                      value={`${dynamicProduct.itemId}`}
                    >
                      {cart.options.channel !== 'mobile' && 'Select'}
                    </Checkbox>
                    {idx === 0 && <span className={cx('frequently-bought-together__fbtFirstItem')}>This Item</span>}
                    <ProductPod
                      brandTitleMaxLine={brandTitleMaxLine}
                      customEventObject={customEventObject}
                      hideATC={hideAddToCart}
                      zipCode={zipCode}
                      hideBulkPrice={hideBulkPrice}
                      podType="default"
                      hideFulfillment={hideFulfillment}
                      hideLimitPerOrder={hideLimitPerOrder}
                      hidePriceBadge={hidePriceBadge}
                      hideRating={hideRating}
                      hideRebates={hideRebates}
                      hideSavingsText={hideSavingsText}
                      hideShippingInfo={hideShippingInfo}
                      hideShopThisCollection={hideShopThisCollection}
                      itemId={itemId}
                      showPreferredPricingBadge
                      key={itemId}
                      position={idx}
                      titleMaxLine={2}
                      showSubscribeAndSave={showSubscribeAndSave}
                      showSeeMoreOptions={showSeeMoreOptions}
                      storeId={storeId}
                    />

                    <span data-id={idx < (data.length - 1) ? 'plus' : 'noPlus'} className={cx('frequently-bought-together__fbtPlus')}>+</span>
                  </QueryProvider>
                </Wrapper>
              );
            }
            )}
          </Carousel>
          <div>
            <PriceMessage
              cartOptions={cart.options}
              cartReqParams={cart.itemDetails}
              channel={cart.options.channel}
              itemId={anchorItemId}
              storeId={storeId}
              message={message}
              onAddToCartClick={onAddToCartClick}
              pricing={totalPrice}
              hideBadge
            />
          </div>
        </Col>
      )}
    </>
  );
};

RecsFBTProductPodCarousel.displayName = 'RecsFBTProductPodCarousel';

RecsFBTProductPodCarousel.propTypes = {
  ...ProductPod.propTypes,
  // eslint-disable-next-line react/forbid-prop-types
  data: any,
  storeId: stringType.isRequired,
  // productPodProps
  // product-pod take only these values for brandTitleMaxLine prop
  brandTitleMaxLine: oneOf([2, 3, null]),
  customEventObject: shapeType({
    component: stringType,
    customEvent: stringType
  }),
  hideATC: boolType,
  zipCode: stringType,
  hideBulkPrice: boolType,
  hideFulfillment: boolType,
  hideLimitPerOrder: boolType,
  hidePriceBadge: boolType,
  hideRating: boolType,
  hideRebates: boolType,
  hideTitle: boolType,
  hideSavingsText: boolType,
  hideShopThisCollection: boolType,
  hideShippingInfo: boolType,
  showSubscribeAndSave: boolType,
  showSeeMoreOptions: boolType,
  keepAddToCart: boolType
};

RecsFBTProductPodCarousel.defaultProps = {
  data: null,
  brandTitleMaxLine: null,
  customEventObject: null,
  hideATC: false,
  zipCode: null,
  hideTitle: false,
  hideBulkPrice: false,
  hideFulfillment: true,
  hideLimitPerOrder: true,
  hidePriceBadge: false,
  hideRating: false,
  showSeeMoreOptions: true,
  hideRebates: true,
  hideSavingsText: false,
  hideShopThisCollection: true,
  hideShippingInfo: true,
  showSubscribeAndSave: false,
  keepAddToCart: null
};

export { RecsFBTProductPodCarousel };
